import { AbortError } from "@captions-web/utils/abort-signals";
import { promiseWithResolvers } from "@captions-web/utils/promise-with-resolvers";
import { createUseStoreWithSelectorDeferred } from "@captions-web/utils/zustand-store";
import { decodeJwt } from "jose";
import { createStore } from "zustand";
import { combine } from "zustand/middleware";

import { setDatadogUser } from "~/lib/analytics/datadog";
import { SessionEvents } from "~/lib/analytics/defs/SessionEvents";
import { getMixpanelStore } from "~/lib/analytics/mixpanel";
import { getStatsigStore } from "~/lib/analytics/statsig";
import { getIntercomStore } from "~/stores/intercom";

import { createStoreGetters } from "../../stores/root";
import { clearStripeKeyMode } from "../checkout/utils";

import { loginCredentials } from "./credentials";
import {
  refreshAccessTokenMutation,
  logoutUserMutation,
  getUserQuery,
  switchOrganizationMutation,
  UserProfileType,
  UserProfileOrgType,
  GetUserProfileResponseType,
} from "./network";

const TOKEN_EXPIRATION_BUFFER_SECONDS = 10; // 10 seconds

interface AuthStoreState {
  user: UserProfileType | null;
  orgs: UserProfileOrgType[];
  currentOrg: UserProfileOrgType | null;

  refreshTokenPromise: Promise<string | null> | null;
}

export function createAuthStore() {
  return createStore(
    combine(
      {
        orgs: [],
        currentOrg: null,
        user: null,
        refreshTokenPromise: null,
      } as AuthStoreState,
      (set, get) => {
        const logout = async (
          reason: "refresh_access_token_failure" | "logout_clicked" | "account_deleted",
          error?: unknown
        ) => {
          SessionEvents.logout.track({ reason, error });
          if (reason === "logout_clicked") {
            await logoutUserMutation.trigger({ params: {} }).catch(console.error);
          }
          loginCredentials.remove();
          set({ user: null });
          getIntercomStore().shutdown();
          // clear the stripe key mode in case it was set to test for an employee account
          // this shouldn't really matter for non-employee accounts, but just in case
          clearStripeKeyMode();
        };

        const refreshAccessToken = async () => {
          const credentials = loginCredentials.get();
          if (!credentials) {
            return null;
          }

          const isTokenStillValid = !isTokenExpired(credentials.accessToken);

          if (isTokenStillValid) {
            return credentials.accessToken;
          }

          if (get().refreshTokenPromise) {
            // if there is already a refresh token promise, return it
            return get().refreshTokenPromise;
          }

          const { promise, resolve } = promiseWithResolvers<string | null>();
          set({ refreshTokenPromise: promise });

          try {
            const authenticationResponse = await refreshAccessTokenMutation.trigger({
              headers: {
                Authorization: `Bearer ${credentials.refreshToken}`,
              },
              // we pass old access token to make sure we authorize against the same org
              params: { oldAccessToken: credentials.accessToken },
            });

            // TODO: we could start timer for refreshing token automatically

            loginCredentials.set({
              accessToken: authenticationResponse.accessToken,
              refreshToken: authenticationResponse.refreshToken,
              userId: credentials.userId,
            });

            resolve(authenticationResponse.accessToken);
            set({ refreshTokenPromise: null });

            return authenticationResponse.accessToken;
          } catch (error) {
            logout("refresh_access_token_failure", error);
            window.location.href = "/";

            // we do not actually reject the stored promise here for the other request
            // since we are redirect the user to the login page anyway.

            // we throw an error so that the request aborts while the browser is redirecting
            throw new AbortError();
          }
        };

        const identifyKoalaUser = () => {
          const { user, currentOrg } = get();

          if (!user || !currentOrg) {
            return;
          }

          window.ko?.identify(user.email, {
            name: user.name,
            email: user.email,
            role: user.role,
            $account: {
              group_id: currentOrg.id,
              name: currentOrg.name,
              plan: currentOrg.subscription?.plan ?? "free",
            },
          });
        };

        const saveProfileResponse = async (response: GetUserProfileResponseType) => {
          const currentUserId = get().user?.userId;
          set({
            user: response.userProfile,
            orgs: [response.orgProfile],
            currentOrg: response.orgProfile,
          });

          if (currentUserId !== response.userProfile.userId) {
            await getStatsigStore().identifyAndSyncUser(
              response.userProfile.userId,
              response.orgProfile.id
            );
            getMixpanelStore().identify(response.userProfile.userId, response.orgProfile.id);
            getIntercomStore().boot(response.userProfile, response.orgProfile);
            identifyKoalaUser();
            setDatadogUser({
              id: response.userProfile.userId,
              email: response.userProfile.email,
              orgId: response.orgProfile.id,
              role: response.userProfile.role,
            });
          }
        };

        async function refreshProfile() {
          const credentials = loginCredentials.get();
          if (!credentials) {
            return null;
          }

          const profileResponse = await getUserQuery.trigger({});
          await saveProfileResponse(profileResponse);

          return { user: profileResponse.userProfile, currentOrg: profileResponse.orgProfile };
        }

        const refreshProfileAfterOrgUpdate = (updates: Partial<UserProfileOrgType>) => {
          const { currentOrg } = get();
          if (!currentOrg) {
            return;
          }

          const updatedOrg = { ...currentOrg, ...updates };
          set({ currentOrg: updatedOrg });

          return refreshProfile();
        };

        return {
          async init() {},
          async ensureProfile() {
            const { user, currentOrg } = get();

            if (user && currentOrg) {
              return { user, currentOrg, wasPreviouslyLoggedIn: true };
            }

            const credentials = loginCredentials.get();
            if (!credentials) {
              return null;
            }

            const profileResponse = await getUserQuery.trigger({});

            await saveProfileResponse(profileResponse);

            return {
              user: profileResponse.userProfile,
              currentOrg: profileResponse.orgProfile,
              wasPreviouslyLoggedIn: false,
            };
          },
          async switchOrganization(orgId: string) {
            const credentials = loginCredentials.get();
            if (!credentials) {
              throw new Error("No credentials found");
            }

            if (orgId === get().currentOrg?.id) {
              return;
            }

            const authResponse = await switchOrganizationMutation.trigger({
              params: {
                orgId,
                refreshToken: credentials.refreshToken,
              },
            });

            loginCredentials.set({
              accessToken: authResponse.accessToken,
              refreshToken: authResponse.refreshToken,
              userId: authResponse.userId,
            });

            const profileResponse = await getUserQuery.trigger({});
            await saveProfileResponse(profileResponse);

            return {
              user: profileResponse.userProfile,
              currentOrg: profileResponse.orgProfile,
            };
          },
          refreshProfile,
          refreshProfileAfterOrgUpdate,
          getIsLikelyLoggedIn() {
            // NOTE: we actually do not want to require access token freshness here
            // because even if the token is expired, we can likely refresh it using
            // the refresh token.
            // if the refresh token is also expired, we will not be able to refresh it
            // and the user will get logged out.
            return !!loginCredentials.get();
          },
          getAccessToken() {
            return refreshAccessToken();
          },
          saveLoginCredentials(credentials: {
            accessToken: string;
            refreshToken: string;
            userId: string;
          }) {
            loginCredentials.set(credentials);
          },
          koalaScriptLoaded() {
            // koala script loads asynchronously, so we need to identify the user after it is loaded
            identifyKoalaUser();
          },
          logout,
        };
      }
    )
  );
}

type AuthStore = ReturnType<ReturnType<typeof createAuthStore>["getState"]>;

export const {
  getStore: getAuthStore,
  getStoreAsync: getAuthStoreAsync,
  getStoreApi: getAuthStoreApi,
} = createStoreGetters<AuthStore>("auth");

export const useAuthStore = createUseStoreWithSelectorDeferred(() => getAuthStoreApi());

function isTokenExpired(token: string) {
  try {
    const decodedToken = decodeJwt(token);
    if (!decodedToken.exp) {
      return true;
    }

    if (typeof decodedToken.exp !== "number") {
      return true;
    }

    const now = Math.floor(Date.now() / 1000);

    return now >= decodedToken.exp - TOKEN_EXPIRATION_BUFFER_SECONDS;
  } catch (error) {
    return true;
  }
}
